<template>
  <div class="minicart-upsells" v-if="products.length > 0">
    <span class="minicart-upsells__title" v-if="title">{{ title }}</span>
    <splide :options="carousel" ref="slider">
      <splide-slide v-for="(product, idx) in products" :key="idx">
        <div class="minicart-upsells__product">
          <a :href="product.url" class="minicart-upsells__image">
            <img :src="product.featured_image" :alt="product.title" width="100" height="150" loading="lazy" />
          </a>
          <div class="minicart-upsells__info">
            <a :href="product.url" class="minicart-upsells__info-title">{{ getProductTitle(product.title) }}</a>
            <p class="minicart-upsells__info-vendor">
              {{ product.vendor }}
            </p>
            <div class="minicart-upsells__info--footer">
              <p class="minicart-upsells__info-price">
                {{ formatPrice(product.price) }}
              </p>
              <button type="button" class="minicart-upsells__info-add" @click="openVariantSelector(product)">
                add
              </button>
            </div>
          </div>
        </div>
      </splide-slide>
    </splide>

    <div class="minicart-upsells__overlay" :class="{ 'is-open': popupOpen }" @click="closePopup"></div>

    <!-- Variant Selector Popup -->
    <div class="minicart-upsells__popup" :class="{ 'is-open': popupOpen }">
      <div class="minicart-upsells__popup-content" v-if="selectedProduct">
        <button type="button" class="minicart-upsells__popup-close" @click="closePopup">
          <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14">
            <polygon
              points="13.759 12.84 7.878 6.958 13.675 1.161 12.968 0.454 7.171 6.251 1.031 0.112 0.324 0.819 6.464 6.958 0.241 13.181 0.948 13.888 7.171 7.665 13.052 13.547 13.759 12.84"></polygon>
          </svg>
        </button>

        <div class="minicart-upsells__popup-product">
          <a :href="selectedProduct.url" class="minicart-upsells__popup-image">
            <img :src="selectedProduct.featured_image" :alt="selectedProduct.title" />
          </a>
          <div class="minicart-upsells__popup-details">
            <a :href="selectedProduct.url" class="minicart-upsells__popup-details--title">
              {{ getProductTitle(selectedProduct.title) }}
            </a>
            <p class="minicart-upsells__popup-details--colour">
              {{ getProductColour(selectedProduct.title) }}
            </p>
            <p class="minicart-upsells__popup-details--vendor">
              {{ selectedProduct.vendor }}
            </p>
            <p class="minicart-upsells__popup-details--price">
              {{ formatPrice(selectedProduct.price) }}
            </p>
          </div>
        </div>

        <!-- Colour Swatches -->
        <div class="minicart-upsells__popup-swatches" v-if="swatches.length > 0">
          <div
            v-for="(swatch, idx) in swatches"
            :key="idx"
            class="minicart-upsells__popup-swatch"
            :class="{
              'is-active': swatch.product.handle === selectedProduct.handle
            }"
            :style="{ backgroundColor: swatch.colour }"
            @click="switchProduct(swatch)"></div>
        </div>

        <!-- Size Options -->
        <div class="minicart-upsells__popup-sizes">
          <button
            v-for="(variant, idx) in selectedProduct.variants"
            :key="idx"
            type="button"
            class="minicart-upsells__popup-size"
            :class="{
              'is-active': selectedVariant && selectedVariant.id === variant.id,
              'sold-out': !variant.available
            }"
            :disabled="!variant.available"
            @click="selectVariant(variant)">
            {{ getVariantSize(variant) }}
          </button>
        </div>

        <!-- Add to Cart Button -->
        <button
          type="button"
          class="minicart-upsells__popup-add-to-cart btn btn-full btn-secondary btn-black"
          :class="{ 'is-loading': isAdding }"
          :disabled="!selectedVariant || isAdding"
          @click="addToCart">
          <span>{{ selectedVariant ? 'add to cart' : 'select options' }}</span>
          <span>{{ formatPrice(selectedVariant ? selectedVariant.price : selectedProduct.price) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { Splide, SplideSlide } from '@splidejs/vue-splide'
import '@splidejs/splide/dist/css/themes/splide-default.min.css'
import { formatMoney } from '@shopify/theme-currency'
import { addItem } from '../../scripts/cart'

export default {
  name: 'MinicartUpsells',

  props: ['products', 'title'],

  components: {
    Splide,
    SplideSlide
  },

  data() {
    return {
      carousel: {
        type: 'slide',
        perPage: 1.5,
        gap: '10px',
        pagination: false,
        arrows: false,
        drag: true,
        focus: 0,
        trimSpace: false,
        breakpoints: {
          990: {
            perPage: 1.3
          }
        }
      },
      popupOpen: false,
      selectedProduct: null,
      selectedVariant: null,
      swatches: [],
      isAdding: false,
      isScrolling: false
    }
  },

  mounted() {
    this.$nextTick(() => {
      const slider = this.$refs.slider
      if (slider && slider.$el) {
        slider.$el.addEventListener('wheel', this.handleWheel, {
          passive: false
        })
      }
    })
  },

  beforeDestroy() {
    const slider = this.$refs.slider
    if (slider && slider.$el) {
      slider.$el.removeEventListener('wheel', this.handleWheel)
    }
  },

  methods: {
    // Scroll slides with trackpad or mouse wheel
    handleWheel(e) {
      const splide = this.$refs.slider?.splide
      if (!splide || this.isScrolling) {
        return
      }

      // Calculate scroll delta threshold
      const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
      if (Math.abs(delta) <= 30) {
        return
      }

      e.preventDefault()
      this.isScrolling = true
      const scrollDirection = delta > 0 ? '>' : '<'
      splide.go(scrollDirection)

      setTimeout(() => {
        this.isScrolling = false
      }, 500)
    },

    getProductTitle(title) {
      return title.split(' / ')[0]
    },

    getProductColour(title) {
      return title.split(' / ')[1] || ''
    },

    getVariantSize(variant) {
      return variant.title.split(' / ')[1] || ''
    },

    formatPrice(price) {
      return formatMoney(price, theme.moneyFormat)
    },

    async openVariantSelector(product) {
      this.selectedProduct = product
      this.selectedVariant = null
      this.swatches = []
      this.popupOpen = true

      // Load swatches
      await this.loadSwatches(product.handle)
    },

    closePopup() {
      this.popupOpen = false

      // Reset product and variant after slide animation
      setTimeout(() => {
        this.selectedProduct = null
        this.selectedVariant = null
        this.swatches = []
      }, 300)
    },

    selectVariant(variant) {
      if (variant.available) {
        this.selectedVariant = variant
      }
    },

    async loadSwatches(handle) {
      try {
        const response = await fetch(`/products/${handle}?view=swatches`)
        const data = await response.json()
        if (data.swatches && data.swatches.length > 0) {
          this.swatches = data.swatches
        }
      } catch (error) {
        console.error('Error loading swatches:', error)
      }
    },

    async switchProduct(swatch) {
      if (swatch.product.handle === this.selectedProduct.handle) return

      try {
        const response = await fetch(`/products/${swatch.product.handle}.js`)
        const product = await response.json()
        this.selectedProduct = product
        this.selectedVariant = null
      } catch (error) {
        console.error('Error switching product:', error)
      }
    },

    async addToCart() {
      if (!this.selectedVariant) return

      this.isAdding = true

      const variantId = Number(this.selectedVariant.id)
      let properties = {}

      if (Number(this.selectedVariant.compare_at_price) > Number(this.selectedVariant.price)) {
        properties = {
          _is_sale: 'true'
        }
      }

      try {
        await addItem(variantId, {
          quantity: 1,
          properties: properties
        })

        this.closePopup()

        // Refresh mini cart
        if (typeof ajaxCart !== 'undefined') {
          ajaxCart.loadMini()
        }
      } catch (error) {
        console.error('Error adding to cart:', error)
        alert('Error adding product to your bag, please try again.')
      } finally {
        this.isAdding = false
      }
    }
  }
}
</script>
